import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Spinner,
} from "reactstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

const invitationCalloutStyle = {
  backgroundColor: "#5e72e4",
  borderColor: "#5e72e4",
  color: "#ffffff",
};

const RegisterForInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [privacyPolicy, setPrivacyPolicy] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const loadInvite = async () => {
      try {
        const response = await Api.get(`/api/account/invite/${token}`);
        setInvite(response.data);
      } catch (e) {
        const status = e.response?.status;
        if (status === 404 || status === 410) {
          setLoadError("This invitation is no longer valid or has expired.");
        } else {
          setLoadError("Unable to load invitation. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadInvite();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!firstName.trim() || !lastName.trim()) {
      setSubmitError("Please enter your first and last name.");
      return;
    }
    if (password.length < 8) {
      setSubmitError("Password must be at least 8 characters.");
      return;
    }
    if (password !== passwordConfirm) {
      setSubmitError("Passwords do not match.");
      return;
    }
    if (!privacyPolicy) {
      setSubmitError("You must agree to the Privacy Policy and Terms & Conditions.");
      return;
    }

    setSubmitting(true);

    try {
      await Api.post("/user/registration", {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: invite.invitedEmail,
        password,
        privacyPolicy: true,
      });

      navigate(`/sign-in?redirect=/invite/${token}`);
    } catch (e) {
      const body = e.response?.data;
      if (typeof body === "string" && body.toLowerCase().includes("already registered")) {
        setSubmitError(
          "This email address is already registered. Please sign in instead."
        );
      } else {
        setSubmitError("Registration failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      );
    }

    if (loadError) {
      return (
        <>
          <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
            Invitation unavailable
          </h2>
          <p className="description mt-3">{loadError}</p>
          <Button color="primary" href="/">
            Return home
          </Button>
        </>
      );
    }

    return (
      <>
        <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
          Create your account
        </h2>
        <div className="alert mt-3 mb-4" style={invitationCalloutStyle}>
          {invite.inviterName} has invited you to join <strong>{invite.accountName}</strong>.
          Create a free account to accept.
        </div>

        {submitError && (
          <div className="alert alert-warning">{submitError}</div>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <InputGroup className="input-group-alternative mb-3">
              <InputGroupText>
                <i className="ni ni-hat-3" />
              </InputGroupText>
              <Input
                placeholder="First name"
                type="text"
                maxLength="50"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <InputGroup className="input-group-alternative mb-3">
              <InputGroupText>
                <i className="ni ni-hat-3" />
              </InputGroupText>
              <Input
                placeholder="Last name"
                type="text"
                maxLength="50"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <InputGroup className="input-group-alternative mb-3">
              <InputGroupText>
                <i className="ni ni-email-83" />
              </InputGroupText>
              <Input
                type="email"
                value={invite.invitedEmail}
                readOnly
                style={{ background: "#f5f5f5" }}
              />
            </InputGroup>
            <small className="text-muted">
              Your account will be created with the email this invitation was sent to.
            </small>
          </FormGroup>
          <FormGroup>
            <InputGroup className="input-group-alternative mb-3">
              <InputGroupText>
                <i className="ni ni-lock-circle-open" />
              </InputGroupText>
              <Input
                placeholder="Password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <InputGroupText
                onClick={() => setShowPassword(p => !p)}
                style={{ cursor: "pointer" }}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </InputGroupText>
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <InputGroup className="input-group-alternative mb-3">
              <InputGroupText>
                <i className="ni ni-lock-circle-open" />
              </InputGroupText>
              <Input
                placeholder="Confirm password"
                type={showPasswordConfirm ? "text" : "password"}
                autoComplete="new-password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
              />
              <InputGroupText
                onClick={() => setShowPasswordConfirm(p => !p)}
                style={{ cursor: "pointer" }}
              >
                {showPasswordConfirm ? <FaEyeSlash /> : <FaEye />}
              </InputGroupText>
            </InputGroup>
          </FormGroup>
          <Row className="my-4">
            <Col xs="12">
              <div className="custom-control custom-control-alternative custom-checkbox">
                <input
                  className="custom-control-input"
                  id="privacyCheck"
                  type="checkbox"
                  checked={privacyPolicy}
                  onChange={(e) => setPrivacyPolicy(e.target.checked)}
                />
                <label className="custom-control-label" htmlFor="privacyCheck">
                  <span>
                    I agree with the <a href="/privacy-policy">Privacy Policy</a> and{" "}
                    <a href="/terms-and-conditions">Terms & Conditions</a>
                  </span>
                </label>
              </div>
            </Col>
          </Row>
          <div className="text-center">
            <Button
              color="primary"
              type="submit"
              className="mt-2"
              disabled={submitting}
            >
              {submitting ? "Creating account..." : "Create account"}
            </Button>
          </div>
        </Form>
      </>
    );
  };

  return (
    <>
      <SeoNoIndex noindex={true} />
      <div className="position-relative">
        <section className="section section-lg section-shaped pb-250">
          <div className="shape shape-style-1 shape-default">
            <span /><span /><span /><span /><span />
            <span /><span /><span /><span />
          </div>
          <Container className="py-lg-md d-flex">
            <div className="col px-0">
              <Row>
                <Col lg="8">
                  <h1 className="display-3 text-white">
                    Join your group{" "}
                    <span>create your account</span>
                  </h1>
                </Col>
              </Row>
            </div>
          </Container>
          <div className="separator separator-bottom separator-skew">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              version="1.1"
              viewBox="0 0 2560 100"
              x="0"
              y="0"
            >
              <polygon className="fill-white" points="2560 0 2560 100 0 100" />
            </svg>
          </div>
        </section>
      </div>
      <section className="section section-lg pt-lg-0 mt--200">
        <Container>
          <Row className="justify-content-center">
            <Col lg="6">
              <Card className="card-lift--hover shadow border-0">
                <CardBody className="py-5">{renderContent()}</CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default RegisterForInvite;
